<template>
  <div class="actions">
    <a-dropdown :trigger="['click']">
      <a-button
        class="c-remove-button"
        type="link"
        icon="delete"
        v-show="availableActions.length"
      />
      <a-menu slot="overlay">
        <a-menu-item
          v-for="(action, i) in availableActions"
          :key="i"
          @click="action.method"
        >
          <span>{{ action.label }}</span>
        </a-menu-item>
      </a-menu>
    </a-dropdown>
  </div>
</template>

<script>
import { ReservationSlot, Availability } from '@/api'
import moment from 'moment'

export default {
  data() {
    return {
      every: false,
      cancellation_reason: ''
    }
  },
  props: {
    calendarSlot: {
      type: Object,
      required: true
    }
  },
  computed: {
    availableActions() {
      const actions = []

      if (this.canCancelReservation) {
        actions.push({
          method: () => this.cancelReservationConfirm(),
          label: 'Odwołaj tę rezerwację'
        })
      }

      if (this.canCancelEveryReservation) {
        actions.push({
          method: () => this.cancelReservationConfirm(true),
          label: this.cancelEveryReservationLabel
        })
      }

      if (this.canCancelAvailability) {
        actions.push({
          method: () => this.cancelSlot(),
          label: 'Usuń wybraną dostępność'
        })
      }

      if (this.canCancelEveryAvailability) {
        actions.push({
          method: () => this.cancelSlot(true),
          label: this.cancelEveryAvailabilityLabel
        })
      }

      return actions
    },
    canCancelAvailability() {
      if (this.isReservation || this.isStudent) {
        return false
      }
      return this.isInFuture || this.isAdmin
    },
    canCancelEveryAvailability() {
      if (this.isStudent) {
        return false
      }
      return this.isInFuture && !this.isReservation && this.isPeriodic
    },
    canCancelReservation() {
      if (!this.isReservation) {
        return false
      }
      return this.isInFuture || this.isAdmin
    },
    canCancelEveryReservation() {
      return this.isInFuture && this.isReservation && this.isReservationPeriodic
    },
    isInFuture() {
      return moment(this.calendarSlot.start).isAfter(moment())
    },
    isReservation() {
      return !!this.calendarSlot.reserved_slot
    },
    isPeriodic() {
      return !!this.calendarSlot.availability.interval
    },
    isReservationPeriodic() {
      return !!this.calendarSlot.reserved_slot.reservation__interval
    },
    cancelEveryAvailabilityLabel() {
      const date = moment(this.calendarSlot.start)
      const start = moment(this.calendarSlot.availability.timespan.lower)
      const end = moment(this.calendarSlot.availability.timespan.upper)
      const dayName = date.format('dddd')
      const time = `${start.format('HH:mm')} - ${end.format('HH:mm')}`
      return `Usuń wybraną oraz każdą kolejną dostępnośc w ${dayName} ${time}`
    },
    cancelEveryReservationLabel() {
      const start = moment(this.calendarSlot.reserved_slot.timespan.lower)
      const end = moment(this.calendarSlot.reserved_slot.timespan.upper)
      const dayName = start.format('dddd')
      const time = `${start.format('HH:mm')} - ${end.format('HH:mm')}`
      return `Odwołaj wybraną oraz każdą kolejną rezerwację w ${dayName} ${time}`
    }
  },
  methods: {
    cancelReservationConfirm(every) {
      const hoursToSlot = moment(this.calendarSlot.start).diff(
        moment(),
        'hours'
      )

      const isPaidTime = hoursToSlot >= 0 && hoursToSlot <= 24
      const isConfirmed = this.calendarSlot.slot_type === 'confirmed'

      const isCharged = isPaidTime && !this.isAdmin && isConfirmed

      this.cancellation_reason = ''

      const onOk = () => {
        this.cancelReservation(every)
      }

      if (isCharged) {
        this.$confirm({
          title: 'Pozostało mniej niż 24h do zajęć',
          maskClosable: true,
          icon: 'warning',
          content: () => (
            <div>
              <p>
                Dokonując anulacji mniej niż 24h przed zajęciami, jesteś
                zobowiązany pokryć koszt zajęć.
              </p>
              <span class="faked-label">Napisz poniżej powód rezygnacji</span>
              <a-input type="textarea" v-model={this.cancellation_reason} />
            </div>
          ),
          onOk
        })
      } else {
        this.$confirm({
          title:
            'Poinformuj nas, jaki jest powód anulacji lub Anuluj rezerwację bez podania powodu',
          width: 480,
          maskClosable: true,
          class: 'custom-cancel-modal',
          okText: 'Anuluj rezerwację',
          cancelText: 'Wróć',
          content: () => (
            <a-input type="textarea" v-model={this.cancellation_reason} />
          ),
          onOk
        })
      }
    },
    cancelReservation(every) {
      ReservationSlot.cancel({
        slotId: this.calendarSlot.reserved_slot.id,
        cancellation_reason: this.cancellation_reason,
        every
      })
        .then(() => {
          this.$emit('reservationCancelled')
          this.$notification.success({ message: 'Rezerwacja odwołana' })
        })
        .catch(() => {
          this.$notification.error({ message: 'Nie można anulować rezerwacji' })
        })
    },
    cancelSlot(every) {
      Availability.cancel({
        availabilityId: this.calendarSlot.availability.id,
        timespan: {
          lower: this.calendarSlot.start,
          upper: this.calendarSlot.end
        },
        every
      })
        .then(() => {
          this.$emit('slotCancelled')
          this.$notification.success({ message: 'Dostępność usunięta' })
        })
        .catch(err => {
          if (err.response.status == 400) {
            const non_field_errors = err.response.data.non_field_errors
            this.$notification.error({
              message: `Wystąpił bład podczas anulowania dostepności. ${non_field_errors}`
            })
          }
        })
    }
  }
}
</script>

<style scoped lang="scss">
.actions {
  position: absolute;
  right: 8px;
  top: 8px;

  & button {
    font-size: 16px;
  }
}
</style>

<style lang="scss">
.custom-cancel-modal {
  .ant-modal-confirm-title {
    align-self: center;
  }

  .ant-modal-confirm-body {
    grid-template-columns: 72px 1fr;
    grid-template-areas: 'icon title' 'icon title' 'text text';
    padding-bottom: 0;
  }

  .ant-modal-confirm-content {
    margin-top: 24px;
  }

  .ant-modal-body {
    background-color: transparent;
    padding-bottom: 0;
  }
}
</style>
